/* The counterparty card's Payments tab — the Orders tab's sibling (`OrdersTab.js`): the
   counterparty's payments from the last 90 days, newest first, in the shared embedded `DocsTab`
   (paging, error + retry, first-load skeleton). Its section heading (`ReadOnlyTag`, `TabHead` with
   no "All …" button — there is no payments list to go to) is `tabParts.js`.

   ⚠ The window is `date_from` alone — `metrics/`'s `window_from`, as the Orders tab, so the list
   waits for it; the API's own `-datetime` default is the "newest first".

   A row opens nothing: a payment has no page and no modal, so `clickableRows={false}` drops the
   row's click and its pointer cursor. The links in a row (who received it, the visit) go through
   the card's `leaveTo`, which asks before unsaved edits are thrown away. A debt still in force
   links to the card's own Debts tab (`debtTo`, from `Page.js`: `?tab=debts&debt=<id>`, which
   scrolls to that debt and washes it). That is a tab switch — the same `history.replace` the rail
   does, on the same route — so the card stays mounted and unsaved edits survive without a
   `leaveTo`. A modified click (new browser tab) is left to the browser.

   Above the table, under the section heading: the response's own `summary` — the count and the
   sum over the whole filtered window, not over the page — and nothing on an empty window. */

import React from "react";
import { Link, useHistory } from "react-router-dom";
import { Flex, Tooltip, Typography } from "antd";

import { dash } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE } from "../../config/lang";
import dayjs from "../../util/dayjs";
import { formatMoney } from "../../util/formatter";
import { translate } from "../../util/util";

import DocsTab from "./DocsTab";
import { AuthorCell, FillCell, VisitCell, linkClick } from "./tabParts";

export default function PaymentsTab({ counterpartyId, lang, leaveTo, debtTo, windowFrom, windowError, onRetryWindow }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const history = useHistory();

  /* A tab switch inside the card (`replace`, as the rail does), not a way out of it — so no
     `leaveTo`; `linkClick` leaves a modified click (new tab) to the browser. */
  const openDebt = to =>
    linkClick((event, target) => {
      event.preventDefault();
      history.replace(target);
    }, to);

  /* The Orders tab's content-sized layout (`lp-fit`, `scroll.x: "max-content"`): the date and the
     amount keep pixel widths, the text columns take what their content needs, and the comment —
     last, in a `FillCell` — takes the card's spare room. The date is pinned, as the Orders tab's
     number. No currency and no colour on the amount: `/sales/orders` shows none either. */
  const columns = [
    {
      key: "datetime",
      title: t("p_date"),
      width: 110,
      fixed: "left",
      render: (v, row) =>
        row.datetime ? (
          <Tooltip title={dayjs(row.datetime).format("DD.MM.YYYY HH:mm")}>
            <span className="lp-num">{dayjs(row.datetime).format("DD.MM.YYYY")}</span>
          </Tooltip>
        ) : (
          dash
        ),
    },
    {
      key: "amount",
      title: t("doc_amount"),
      width: 130,
      align: "right",
      render: (v, row) => <span className="lp-num">{formatMoney(row.amount) || dash}</span>,
    },
    {
      /* A debt in force links to the Debts tab (see above). A removed one is not listed there, so
         it stays text — its name as the data has it, with no mark of our own. */
      key: "debt",
      title: t("p_debt"),
      render: (v, row) =>
        row.debt && !row.debt.is_removed && row.debt.name_of_the_document ? (
          <Typography.Text ellipsis={{ tooltip: row.debt.name_of_the_document }}>
            <Link className="cp-doclink cp-doclink--plain cp-doclink--inline" to={debtTo(row.debt.id)} onClick={openDebt(debtTo(row.debt.id))}>
              {row.debt.name_of_the_document}
            </Link>
          </Typography.Text>
        ) : row.debt ? (
          <Typography.Text ellipsis={{ tooltip: row.debt.name_of_the_document }}>{row.debt.name_of_the_document || dash}</Typography.Text>
        ) : (
          <Typography.Text type="secondary" ellipsis={{ tooltip: t("p_no_debt") }}>
            {t("p_no_debt")}
          </Typography.Text>
        ),
    },
    /* "—" without a creator: 1C creates no payments, so the Orders tab's "from 1C" does not apply. */
    { key: "created_by", title: t("p_accepted_by"), render: (v, row) => <AuthorCell row={row} leaveTo={leaveTo} /> },
    { key: "visit", title: t("o_visit"), render: (v, row) => <VisitCell row={row} lang={lang} /> },
    {
      key: "comment",
      title: t("doc_comment"),
      render: (v, row) =>
        row.comment ? (
          <FillCell full={row.comment} type="secondary">
            {row.comment}
          </FillCell>
        ) : (
          dash
        ),
    },
  ];

  const renderSummary = summary => (
    <Flex className="cp-docstab__sum" align="center" gap="large" wrap>
      <span>
        <Typography.Text type="secondary">{t("p_sum_count")}</Typography.Text> <span className="lp-num">{summary.count}</span>
      </span>
      <span>
        <Typography.Text type="secondary">{t("p_sum_total")}</Typography.Text> <span className="lp-num">{formatMoney(summary.sum) || dash}</span>
      </span>
    </Flex>
  );

  return (
    <DocsTab
      counterpartyId={counterpartyId}
      endpoint="excalibur/payments/"
      columns={columns}
      emptyText={t("payments_empty")}
      lang={lang}
      query={windowFrom ? { date_from: windowFrom } : null}
      waitError={windowError}
      onRetryWait={onRetryWindow}
      clickableRows={false}
      renderSummary={renderSummary}
      /* Content-sized, as the Orders tab. */
    />
  );
}
